import { useCallback, useContext, useMemo } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { toast } from 'react-toastify';
import {
  buildSquareBillingContact,
  getSquareApplicationErrorMessage,
  isBuyerVerificationError,
  toSquareAmount,
} from '../square.utils';
import { useSelector } from 'react-redux';
import { DARK_THEME } from 'theme.constants';
import { selectors } from 'redux/app/app.slice';
import { selectCustomerInfo } from 'redux/cart/cart.selectors';
import {
  selectEmail,
  selectFirstName,
  selectLastName,
  selectPhoneNumber,
} from 'redux/user/user.selectors';
import getSquare from 'util/getSquare';
import { NewPaymentContext } from 'components/payment/payment.context';
import { useSquareContext } from '../square.context';

const toastTokenizeErrors = errors =>
  errors?.forEach(e => toast.error(getSquareApplicationErrorMessage(e)));

/**
 * 'Invalid' means the shopper's own entry failed validation - the card form
 * already shows that inline, so reporting it again would double up.
 */
const readTokenResult = tokenResult => {
  if (tokenResult?.status === 'OK') return tokenResult.token;

  if (tokenResult?.status !== 'Invalid')
    toastTokenizeErrors(tokenResult?.errors);

  return undefined;
};

/**
 * Builds the verificationDetails that Square's tokenize() uses to run Strong
 * Customer Authentication. Returns a builder rather than an object so callers
 * can pick the intent, and so gift card flows can pass their own amount instead
 * of the order total.
 */
export const useSquareVerificationDetails = () => {
  const { initOrderResponse, amount } = useContext(NewPaymentContext) ?? {};

  // Checkout fills the cart's customerInfo, including for guests, but screens
  // like My Cards have no cart at all. Square needs a populated billingContact
  // to verify a buyer - an empty one fails outright - so fall back to the
  // signed-in profile field by field.
  const customerInfo = useSelector(selectCustomerInfo);
  const firstName = useSelector(selectFirstName);
  const lastName = useSelector(selectLastName);
  const email = useSelector(selectEmail);
  const phoneNumber = useSelector(selectPhoneNumber);

  const billingContact = useMemo(
    () =>
      buildSquareBillingContact({
        firstName: customerInfo?.firstName ?? firstName,
        lastName: customerInfo?.lastName ?? lastName,
        email: customerInfo?.email ?? email,
        phoneNumber: customerInfo?.phoneNumber ?? phoneNumber,
      }),
    [customerInfo, firstName, lastName, email, phoneNumber],
  );

  const currencyCode = initOrderResponse?.attributes?.currencycode ?? 'USD';

  return useCallback(
    ({ intent = 'CHARGE', amountInMilliunits = amount } = {}) => {
      const details = {
        intent,
        // Every Square payment we make originates from a live checkout, so the
        // buyer is always present and never reading their card out to us.
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact,
      };

      // STORE charges nothing, so it carries no amount to bind the token to.
      if (intent !== 'STORE') {
        details.amount = toSquareAmount(amountInMilliunits);
        details.currencyCode = currencyCode;
      }

      return details;
    },
    [amount, currencyCode, billingContact],
  );
};

/**
 * Tokenizes the card the shopper just typed into the mounted Square form.
 * Passing verificationDetails is what runs SCA - the token that comes back
 * carries the verification, which is why we never send a separate
 * verification token from web.
 */
export const useSquareCardTokenize = () => {
  const { squareCard } = useSquareContext();
  const handleError = useErrorHandler();

  const tokenize = useCallback(
    async verificationDetails => {
      if (!squareCard) return;

      try {
        const tokenResult = await squareCard.tokenize(verificationDetails);
        return readTokenResult(tokenResult);
      } catch (e) {
        if (isBuyerVerificationError(e)) return;

        toastTokenizeErrors(e?.errors);
        handleError(e);
      }
    },
    [squareCard, handleError],
  );

  return tokenize;
};

export const useGetSqaureCard = () => {
  const theme = useSelector(selectors.selectTheme);
  const handleError = useErrorHandler();
  const { initOrderResponse } = useContext(NewPaymentContext);

  const { applicationId, locationId } = initOrderResponse?.attributes ?? {};

  const cardOptions = useMemo(
    () => ({
      style:
        theme === DARK_THEME
          ? {
              '.message-text': {
                color: 'white',
              },
              '.message-icon': {
                color: 'white',
              },
            }
          : {},
    }),
    [theme],
  );

  const getCard = useCallback(async () => {
    const square = getSquare();

    if (!square) return;

    try {
      const payments = square.payments(applicationId, locationId);
      return await payments.card(cardOptions);
    } catch (e) {
      handleError(e);
    }
  }, [cardOptions, handleError, applicationId, locationId]);

  return getCard;
};

/**
 * Re-tokenizes a card already stored with Square so the charge can carry SCA.
 *
 * The saved-card screen renders no card form, so there is no Card in context to
 * tokenize with. Square's card-on-file flow only needs a Card object, not a
 * mounted one, so we build a throwaway, tokenize the stored card id through it,
 * and tear it down.
 *
 * What comes back is a fresh single-use token - NOT the stored card id - and
 * that is what has to go to the backend as paymentToken.
 */
export const useSquareTokenizeStoredCard = () => {
  const getCard = useGetSqaureCard();
  const handleError = useErrorHandler();

  return useCallback(
    async (verificationDetails, storedCardId) => {
      if (!storedCardId) return;

      let card;

      try {
        card = await getCard();
        if (!card) return;

        const tokenResult = await card.tokenize(
          verificationDetails,
          storedCardId,
        );

        return readTokenResult(tokenResult);
      } catch (e) {
        if (isBuyerVerificationError(e)) return;

        toastTokenizeErrors(e?.errors);
        handleError(e);
      } finally {
        try {
          await card?.destroy();
        } catch {
          // a card that was never attached can refuse to destroy; nothing to do
        }
      }
    },
    [getCard, handleError],
  );
};
